/**
 * QuickAddTemplates - Phase 5
 * 
 * Horizontal scrolling widget for one-tap template adding.
 * Shows popular templates as quick-access cards.
 * 
 * @module QuickAddTemplates
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, ChevronRight, ChevronLeft } from 'lucide-react';
import { getTemplateCategories, generateTasksFromTemplate } from '@/constants/taskTemplates';

// ============================================================================
// STYLES
// ============================================================================

const styles = {
  container: {
    marginBottom: '24px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
    paddingRight: '8px',
  },
  title: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  viewAll: {
    fontSize: '13px',
    color: '#667eea',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: '4px 8px',
    borderRadius: '6px',
    transition: 'background 0.2s',
  },
  scrollContainer: {
    display: 'flex',
    gap: '12px',
    overflowX: 'auto',
    paddingBottom: '8px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  templateCard: {
    flexShrink: 0,
    width: '120px',
    padding: '12px 10px',
    borderRadius: '12px',
    borderLeft: '3px solid',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    transition: 'all 0.2s',
    position: 'relative',
  },
  templateIcon: {
    fontSize: '24px',
    marginBottom: '6px',
  },
  templateName: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '2px',
    lineHeight: 1.2,
  },
  taskCount: {
    fontSize: '10px',
    color: '#9ca3af',
  },
  addBadge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    opacity: 0,
    transform: 'scale(0.8)',
    transition: 'all 0.2s',
  },
  addingIndicator: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
  },
  emptyState: {
    padding: '24px',
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: '13px',
  },
  scrollWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  navArrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.95)',
    border: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 10,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s',
    color: '#374151',
  },
  navArrowLeft: {
    left: '-8px',
  },
  navArrowRight: {
    right: '-8px',
  },
};

// Template color to border/background mapping
const TEMPLATE_COLORS = {
  emerald: { border: '#10b981', bg: 'rgba(16, 185, 129, 0.05)', hover: 'rgba(16, 185, 129, 0.1)' },
  blue: { border: '#3b82f6', bg: 'rgba(59, 130, 246, 0.05)', hover: 'rgba(59, 130, 246, 0.1)' },
  purple: { border: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.05)', hover: 'rgba(139, 92, 246, 0.1)' },
  amber: { border: '#f59e0b', bg: 'rgba(245, 158, 11, 0.05)', hover: 'rgba(245, 158, 11, 0.1)' },
  red: { border: '#ef4444', bg: 'rgba(239, 68, 68, 0.05)', hover: 'rgba(239, 68, 68, 0.1)' },
  green: { border: '#22c55e', bg: 'rgba(34, 197, 94, 0.05)', hover: 'rgba(34, 197, 94, 0.1)' },
  teal: { border: '#14b8a6', bg: 'rgba(20, 184, 166, 0.05)', hover: 'rgba(20, 184, 166, 0.1)' },
  indigo: { border: '#6366f1', bg: 'rgba(99, 102, 241, 0.05)', hover: 'rgba(99, 102, 241, 0.1)' },
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const QuickAddTemplates = ({ 
  onAddTemplate, 
  onViewAll, 
  listId,
  maxDisplay = 5,
}) => {
  const [addingId, setAddingId] = useState(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const scrollContainerRef = useRef(null);
  const templates = getTemplateCategories().slice(0, maxDisplay);

  // Check scroll position to show/hide arrows
  const checkScrollPosition = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 10);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      checkScrollPosition();
      container.addEventListener('scroll', checkScrollPosition);
      return () => container.removeEventListener('scroll', checkScrollPosition);
    }
  }, [templates]);

  const scrollBy = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const scrollAmount = 140; // Width of one card + gap
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  const handleQuickAdd = async (template, e) => {
    e.stopPropagation();
    
    if (!listId || addingId) return;
    
    setAddingId(template.id);
    try {
      const tasks = generateTasksFromTemplate(template, listId);
      await onAddTemplate(template, tasks);
    } catch (error) {
      console.error('[QuickAddTemplates] Error:', error);
    } finally {
      setAddingId(null);
    }
  };

  if (templates.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          No templates available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
          <span>📋</span>
          Quick Templates
        </div>
        <button 
          onClick={onViewAll}
          className="text-sm text-indigo-500 dark:text-indigo-400 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
        >
          View All
          <ChevronRight size={14} />
        </button>
      </div>

      {/* Horizontal Scroll */}
      <div style={styles.scrollWrapper}>
        <div 
          ref={scrollContainerRef}
          style={styles.scrollContainer}
          className="hide-scrollbar"
        >
        {templates.map((template) => {
          const isAdding = addingId === template.id;
          const colorConfig = TEMPLATE_COLORS[template.color] || TEMPLATE_COLORS.blue;

          return (
            <motion.div
              key={template.id}
              style={{
                ...styles.templateCard,
                borderLeftColor: colorConfig.border,
              }}
              className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600"
              whileHover={{ 
                scale: 1.03, 
                boxShadow: `0 4px 12px ${colorConfig.border}30`,
              }}
              whileTap={{ scale: 0.97 }}
              onClick={(e) => handleQuickAdd(template, e)}
              onMouseEnter={(e) => {
                const badge = e.currentTarget.querySelector('.add-badge');
                if (badge) {
                  badge.style.opacity = '1';
                  badge.style.transform = 'scale(1)';
                }
              }}
              onMouseLeave={(e) => {
                const badge = e.currentTarget.querySelector('.add-badge');
                if (badge) {
                  badge.style.opacity = '0';
                  badge.style.transform = 'scale(0.8)';
                }
              }}
            >
              <span style={styles.templateIcon}>{template.icon}</span>
              <div className="text-xs font-semibold text-gray-800 dark:text-white mb-0.5 leading-tight">{template.name}</div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400">{template.tasks.length} tasks</div>
              
              {/* Add Badge */}
              <div className="add-badge" style={styles.addBadge}>
                <Plus size={14} />
              </div>

              {/* Adding Indicator */}
              {isAdding && (
                <motion.div
                  style={styles.addingIndicator}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    ⏳
                  </motion.span>
                </motion.div>
              )}
            </motion.div>
          );
        })}

        {/* View More Card */}
        {getTemplateCategories().length > maxDisplay && (
          <motion.div
            style={{
              ...styles.templateCard,
              borderStyle: 'dashed',
              borderLeftColor: '#9ca3af',
            }}
            className="bg-gray-50 dark:bg-slate-700/50 border border-dashed border-gray-300 dark:border-slate-500"
            whileHover={{ 
              scale: 1.03,
            }}
            whileTap={{ scale: 0.97 }}
            onClick={onViewAll}
          >
            <span style={{ ...styles.templateIcon, opacity: 0.6 }}>➕</span>
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-300 mb-0.5 leading-tight">
              More Templates
            </div>
            <div className="text-[10px] text-gray-400 dark:text-gray-500">
              +{getTemplateCategories().length - maxDisplay}
            </div>
          </motion.div>
        )}
        </div>
      </div>

      {/* Scroll Indicator Dots */}
      <div className="flex items-center justify-center gap-1.5 pt-2">
        <span 
          className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
            showLeftArrow 
              ? 'bg-gray-300 dark:bg-slate-600' 
              : 'bg-[#006D77] dark:bg-[#83C5BE] scale-125'
          }`}
        />
        <span 
          className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
            showLeftArrow && showRightArrow 
              ? 'bg-[#006D77] dark:bg-[#83C5BE] scale-125' 
              : 'bg-gray-300 dark:bg-slate-600'
          }`}
        />
        <span 
          className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
            showRightArrow 
              ? 'bg-gray-300 dark:bg-slate-600' 
              : 'bg-[#006D77] dark:bg-[#83C5BE] scale-125'
          }`}
        />
      </div>
    </div>
  );
};

export default QuickAddTemplates;
